import { useEffect } from 'react'
import type { PipelineCatalogEntry } from 'app/components/action-center/usePipelinesCatalog'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useSystemListStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import { useWorkspace } from 'app/utils'
import { isOnActiveLeads } from 'lib/activeLeadMembership'
import { listCatalogEntry } from 'lib/listMemberships'
import { legacyPipelineIdOf, SYSTEM_LIST_BY_SLUG, systemListId } from 'lib/systemLists'

const ACTIVE_LEADS = SYSTEM_LIST_BY_SLUG.get(`active-leads`)!
const LEGACY_PIPELINE_ID = legacyPipelineIdOf(ACTIVE_LEADS)

type ActiveLeadMembership = {
  catalog: PipelineCatalogEntry[]
  entry: { id: string; stage: string } | undefined
  /** Hidden whether or not the list has loaded, so the old pipeline chip never flashes first. */
  replacing: string | undefined
}

/** A contact's place on Active Leads, read-only: Lead Status is the control that moves it. */
export function useActiveLeadMembership(contact: ContactJson | undefined): ActiveLeadMembership {
  const store = useSystemListStore()
  const workspaceId = useWorkspace()?.id
  const needsActionDateEnabled = !!useFeatureToggle(`needs-action-date`)
  const listId = workspaceId && contact ? systemListId(ACTIVE_LEADS, workspaceId) : undefined

  useEffect(() => {
    if (listId) store.ensure(listId)
  }, [listId, store])

  const list = listId ? store.get(listId) : undefined
  const replacing = listId ? LEGACY_PIPELINE_ID : undefined
  if (!list || !contact) return { catalog: [], entry: undefined, replacing }
  return {
    catalog: [listCatalogEntry(list)],
    entry: isOnActiveLeads(contact, needsActionDateEnabled) ? { id: list.id, stage: contact.leadStatus! } : undefined,
    replacing,
  }
}
